import {Injectable} from '@angular/core';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {Observable, ReplaySubject} from 'rxjs';
import {TypedDataCollectionInterface} from 'shared/models/octopus-connect/typed-data-collection.interface';
import {TypedDataEntityInterface} from 'shared/models/octopus-connect/typed-data-entity.interface';
import {CommunicationCenterService} from '@modules/communication-center';
import {UserDataEntity} from '@modules/authentication/core/models/user-data-entity.type';
import {catchError, map, mergeMap, take, tap} from 'rxjs/operators';
import {throwError} from 'rxjs/internal/observable/throwError';

const LICENSE_ENDPOINT = 'licenses';

export enum LicenseType {
    class = '4045',
    institution = '4044',
}

export interface LicenseTypeInterface {
    'id': string | number;
    'label': string;
}

export interface LicenseDataInterface {
    'id': string | number;
    'code': string;
    // TODO si utilisé
    'access': unknown;
    'date': Date;
    'expirationDate': number;
    // User id
    'uid': string | number;
    'expired': boolean;
    'type'?: LicenseTypeInterface;
}

export type LicenseDataEntity = TypedDataEntityInterface<LicenseDataInterface>;
export type LicenseDataCollection = TypedDataCollectionInterface<LicenseDataInterface>;

@Injectable({
    providedIn: 'root'
})
export class LicensesService {

    private _licensesLoaded = false;
    public currentUserLicenses: LicenseDataEntity[] = [];
    public currentUserLicenses$ = new ReplaySubject<LicenseDataEntity[]>(1);

    constructor(
        private octopusConnect: OctopusConnectService,
        private communicationCenter: CommunicationCenterService
    ) {
        this.communicationCenter
            .getRoom('groups-management')
            .getSubject('requestLicenses')
            .subscribe(() => this.requestLicences());

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: UserDataEntity) => {
                if (data) {
                    this.postAuthentication();
                } else {
                    this.postLogout();
                }
            });
    }

    public requestLicences() {
        if (!this._licensesLoaded) {
            this.loadUserLicenses$().pipe(take(1)).subscribe();
        } else {
            this.communicationCenter
                .getRoom('groups-management')
                .next('userLicenses', this.currentUserLicenses.slice());
        }
    }

    public patchLicense(license: { id: string; startDate: number; endDate: number; type: string }, change: {
        type: string;
        endDate: number
    }): Observable<DataEntity> {
        const license$ = this.octopusConnect.loadEntity(LICENSE_ENDPOINT, license.id);
        return license$.pipe(
            take(1),
            mergeMap((licenseEntity: DataEntity) => {
                licenseEntity.set('type', LicenseType[change.type]);
                licenseEntity.set('expirationDate', change.endDate && change.endDate.toString() || null);
                return licenseEntity.save(true);
            })
        );
    }

    public createLicense(param: { endDate: number; type: string, userId: string }): Observable<DataEntity> {
        return this.octopusConnect.createEntity(LICENSE_ENDPOINT, {
            uid: param.userId,
            type: LicenseType[param.type],
            expirationDate: param.endDate,
            date: Math.round(new Date().getTime() / 1000),
        });
    }

    public getAllLicenses(forceToLoadLicenses = false): Observable<LicenseDataEntity[]> {
        return this.getCurrentUserLicenses$(forceToLoadLicenses)
            .pipe(map((licenseEntities) => licenseEntities.slice()));
    }

    public getStillActiveLicenses(forceToLoadLicenses = false): Observable<LicenseDataEntity[]> {
        return this.getCurrentUserLicenses$(forceToLoadLicenses).pipe(
                map(licenseEntities => licenseEntities.filter((l) => {
                    const expirationDate = new Date(l.attributes.expirationDate * 1000)
                    return expirationDate > new Date()
                }))
            )
    }

    private getLicensesOfCurrentUser$(): Observable<LicenseDataCollection> {
        // Doublon avec le licensing module, mais il n'a pas le meme intérêt
        return this.octopusConnect.loadCollection(LICENSE_ENDPOINT) as Observable<LicenseDataCollection>;
    }

    private postAuthentication(): void {
        this.currentUserLicenses.length = 0;
        this.currentUserLicenses$ = new ReplaySubject<LicenseDataEntity[]>(1);
        //this.loadUserLicenses$().subscribe();
    }

    private postLogout(): void {
        this.currentUserLicenses = [];
        this.currentUserLicenses$.next([]);
    }

    private getCurrentUserLicenses$(forceToLoadLicenses = false): Observable<LicenseDataEntity[]> {
        return this._licensesLoaded && !forceToLoadLicenses
            ? this.currentUserLicenses$
            : this.loadUserLicenses$().pipe(map(col => col.entities.slice()));
    }

    private loadUserLicenses$(): Observable<LicenseDataCollection> {
        this._licensesLoaded = true;
        return this.getLicensesOfCurrentUser$()
            .pipe(
                tap(licenseCollection => {
                    this.currentUserLicenses = licenseCollection.entities.slice();
                    // Publier directement dans la room
                    this.communicationCenter
                        .getRoom('groups-management')
                        .next('userLicenses', this.currentUserLicenses.slice());
                }),
                catchError(error => {
                    this._licensesLoaded = false;
                    return throwError(error);
                })
            );
    }
}
